import { ROAD_HALF_WIDTH, type RoadPath } from './road-paths';
/** Main-equivalent clearance is only needed inside the largest terrain/prop buffer (3 WU).
 * Taking min(distance + mainHalfWidth - pathHalfWidth) across every segment retains
 * the union of wide main and narrower trail buffers, including overlapping junctions. */
type Point = { x: number; y: number };
type Segment = { a: Point; b: Point; widthOffset:number };
type RoadIndex = { cells: Map<string, Segment[]>; overflow: Segment[]; hasSegments: boolean };
const CELL_SIZE = 32;
const CLEARANCE_LIMIT = 3;
const networkIndexes = new WeakMap<readonly (readonly Point[])[], RoadIndex>();
const styledIndexes = new WeakMap<readonly RoadPath[], RoadIndex>();
const routeIndexes = new WeakMap<readonly Point[], RoadIndex>();

function buildIndex(paths: readonly RoadPath[]): RoadIndex {
  const result: RoadIndex = { cells: new Map(), overflow: [], hasSegments: false };
  for (const {points:path,kind} of paths) for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!, b = path[i]!;
    if (![a.x, a.y, b.x, b.y].every(Number.isFinite)) continue;
    result.hasSegments = true;
    const segment = { a, b, widthOffset:ROAD_HALF_WIDTH.main-ROAD_HALF_WIDTH[kind] };
    const minX = Math.floor((Math.min(a.x, b.x) - CLEARANCE_LIMIT) / CELL_SIZE);
    const maxX = Math.floor((Math.max(a.x, b.x) + CLEARANCE_LIMIT) / CELL_SIZE);
    const minY = Math.floor((Math.min(a.y, b.y) - CLEARANCE_LIMIT) / CELL_SIZE);
    const maxY = Math.floor((Math.max(a.y, b.y) + CLEARANCE_LIMIT) / CELL_SIZE);
    // Legacy routes can span the world in one segment. Keep their index allocation bounded.
    if ((maxX - minX + 1) * (maxY - minY + 1) > 256) { result.overflow.push(segment); continue; }
    for (let x = minX; x <= maxX; x++) for (let y = minY; y <= maxY; y++) {
      const key = `${x},${y}`, bucket = result.cells.get(key);
      if (bucket) bucket.push(segment); else result.cells.set(key, [segment]);
    }
  }
  return result;
}

function nearbyDistance(x: number, y: number, index: RoadIndex): number {
  let minimum = Infinity;
  const scan = (segments: readonly Segment[]) => {
    for (const { a, b, widthOffset } of segments) {
      if (x < Math.min(a.x, b.x) - CLEARANCE_LIMIT || x > Math.max(a.x, b.x) + CLEARANCE_LIMIT
        || y < Math.min(a.y, b.y) - CLEARANCE_LIMIT || y > Math.max(a.y, b.y) + CLEARANCE_LIMIT) continue;
      const dx = b.x - a.x, dy = b.y - a.y, length2 = dx * dx + dy * dy;
      const t = length2 > 1e-12 ? Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / length2)) : 0;
      const distance = Math.hypot(x - a.x - dx * t, y - a.y - dy * t) + widthOffset;
      if (distance < minimum) minimum = distance;
    }
  };
  scan(index.cells.get(`${Math.floor(x / CELL_SIZE)},${Math.floor(y / CELL_SIZE)}`) ?? []);
  scan(index.overflow);
  return minimum;
}

/** Input arrays are immutable snapshots; replacing a network/route invalidates its cached index. */
export function indexedRoadClearance(x: number, y: number, network: Array<Array<Point>>, route: Array<Point> | null, styles?: readonly RoadPath[]): { distance: number; hasSegments: boolean } {
  let networkIndex = styles ? styledIndexes.get(styles) : networkIndexes.get(network);
  if (!networkIndex) {
    networkIndex = buildIndex(styles ?? network.map(points=>({points,kind:'main'})));
    if(styles)styledIndexes.set(styles,networkIndex);else networkIndexes.set(network,networkIndex);
  }
  let distance = nearbyDistance(x, y, networkIndex), hasSegments = networkIndex.hasSegments;
  if (route) {
    let routeIndex = routeIndexes.get(route);
    if (!routeIndex) { routeIndex = buildIndex([{points:route,kind:'main'}]); routeIndexes.set(route, routeIndex); }
    distance = Math.min(distance, nearbyDistance(x, y, routeIndex));
    hasSegments ||= routeIndex.hasSegments;
  }
  return { distance, hasSegments };
}
